// src/FloatingOrderBanner.jsx
import { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import { T } from './theme'

const ACTIVE = ['placed', 'accepted', 'partner_accepted', 'assigned', 'preparing', 'ready', 'picked_up']

const STATUS_LABEL = {
  placed:           'Order Placed',
  accepted:         'Accepted',
  partner_accepted: 'Accepted',
  assigned:         'Partner Assigned',
  preparing:        '👨‍🍳 Preparing',
  ready:            '📦 Ready',
  picked_up:        '🚴 On the Way',
  cancelled:        '❌ Cancelled',
}
// Colour shifts as the order progresses
const STATUS_COLOR = {
  placed:           T.gold,
  accepted:         T.gold,
  partner_accepted: T.gold,
  assigned:         '#5c9ee0',
  preparing:        T.gold,
  ready:            T.green,
  picked_up:        '#5c9ee0',
  cancelled:        '#e05c5c',
}

export default function FloatingOrderBanner({ user, onTrack }) {
  const [orders, setOrders]   = useState([])
  const [showAll, setShowAll] = useState(false)
  const channelRef            = useRef(null)

  useEffect(() => {
    if (!user?.id) return
    load()
    if (channelRef.current) supabase.removeChannel(channelRef.current)
    channelRef.current = supabase
      .channel(`float-orders-${user.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'delivery_orders',
        filter: `user_id=eq.${user.id}`,
      }, load)
      .subscribe()
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current) }
  }, [user?.id])

  const load = async () => {
    const { data } = await supabase
      .from('delivery_orders')
      .select('id, status, total, order_type, restaurants!delivery_orders_restaurant_id_fkey(name)')
      .eq('user_id', user.id)
      .in('status', ACTIVE)
      .order('created_at', { ascending: false })
    setOrders(data || [])
  }

  if (orders.length === 0) return null
  const primary = orders[0]
  const sc = STATUS_COLOR[primary.status] || T.gold
  return (
    <>
      <style>{`
        @keyframes banner-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.3)} }
        @keyframes banner-slide  { from{transform:translateY(100%);opacity:0} to{transform:translateY(0);opacity:1} }
      `}</style>

      <div style={{
        position: 'fixed', bottom: '16px', left: '16px', right: '16px',
        zIndex: 500, animation: 'banner-slide 0.3s ease',
      }}>
        <div style={{
          background: 'rgba(8,8,8,0.97)',
          border: `1px solid ${sc}`,
          borderRadius: '16px',
          padding: '14px 16px',
          boxShadow: `0 8px 40px rgba(0,0,0,0.85), 0 0 0 1px ${sc}`,
        }}>
          {/* Primary order row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0,
              background: sc, boxShadow: `0 0 8px ${sc}`,
              animation: 'banner-pulse 1.8s ease-in-out infinite',
            }} />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '13px', color: T.white, fontWeight: '600',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {primary.restaurants?.name || 'Restaurant'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                <span style={{ fontSize: '11px', color: sc, fontWeight: 600 }}>
                  {STATUS_LABEL[primary.status] || primary.status}
                </span>
                <span style={{ fontSize: '10px', color: T.dimmed }}>·</span>
                <span style={{ fontSize: '11px', color: T.muted }}>₹{primary.total}</span>
                <span style={{ fontSize: '10px', color: T.dimmed }}>·</span>
                <span style={{ fontSize: '10px', color: T.muted }}>
                  {primary.order_type === 'delivery' ? '🚴' : '🏪'}
                </span>
                {orders.length > 1 && (
                  <>
                    <span style={{ fontSize: '10px', color: T.dimmed }}>·</span>
                    <span style={{ fontSize: '10px', color: T.muted }}>+{orders.length - 1} more</span>
                  </>
                )}
              </div>
            </div>

            <button
              onClick={() => onTrack(primary)}
              style={{
                padding: '9px 18px',
                background: T.gold, border: 'none',
                borderRadius: '10px', color: '#080808',
                fontSize: '12px', fontWeight: '700',
                cursor: 'pointer', fontFamily: T.sans,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              Track →
            </button>
          </div>

          {/* Multiple orders — expand/collapse */}
          {orders.length > 1 && (
            <>
              {showAll && (
                <div style={{
                  marginTop: '10px',
                  borderTop: `1px solid ${T.faint}`,
                  paddingTop: '10px',
                  display: 'flex', flexDirection: 'column', gap: '8px',
                }}>
                  {orders.slice(1).map(order => (
                    <div key={order.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: T.white }}>{order.restaurants?.name}</div>
                        <div style={{ fontSize: '10px', color: T.gold, marginTop: '1px' }}>
                          {STATUS_LABEL[order.status] || order.status} · ₹{order.total}
                        </div>
                      </div>
                      <button
                        onClick={() => onTrack(order)}
                        style={{
                          padding: '5px 14px',
                          background: T.bg3, border: `1px solid ${T.goldBorder}`,
                          borderRadius: '8px', color: T.gold,
                          fontSize: '11px', cursor: 'pointer', fontFamily: T.sans,
                        }}
                      >
                        Track
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => setShowAll(!showAll)}
                style={{
                  width: '100%', marginTop: '8px',
                  background: 'transparent', border: 'none',
                  color: T.muted, fontSize: '10px',
                  cursor: 'pointer', letterSpacing: '1px', fontFamily: T.sans,
                }}
              >
                {showAll
                  ? '▲ Collapse'
                  : `▼ ${orders.length - 1} more active order${orders.length > 2 ? 's' : ''}`}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  )
}